import { createClient } from '@supabase/supabase-js';
import type { Database } from '../integrations/supabase/types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

export type VideoItem = {
  id: string;
  videoId: string;
  title: string;
  thumbnail: string;
};

export type Category = {
  id: string;
  title: string;
  slug: string;
  description?: string;
  created_at?: string;
};

export type Collection = {
  id?: string;
  name: string;
  description?: string;
  thumbnail_url?: string;
  created_at?: string;
  user_id?: string;
  is_public?: boolean;
  public_id?: string;
  tags?: string[];
  categories?: Category[];
  videos?: VideoItem[];
  is_subscribed?: boolean;
};

export const getCategories = async () => {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('title');

  if (error) throw error;
  return data;
};

export const getCategoryBySlug = async (slug: string) => {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) throw error;
  return data;
};

export const getCollectionsByCategory = async (categoryId: string) => {
  const { data: links, error: linkErr } = await supabase
    .from('collection_categories')
    .select('collection_id')
    .eq('category_id', categoryId);

  if (linkErr) throw linkErr;

  const ids = links.map((l) => l.collection_id);
  if (ids.length === 0) return [];

  const { data: collections, error: collectionErr } = await supabase
    .from('collections')
    .select('*')
    .in('id', ids)
    .order('created_at', { ascending: false });

  if (collectionErr) throw collectionErr;

  const { data: videos, error: videosErr } = await supabase
    .from('collection_videos')
    .select('*')
    .in('collection_id', ids);

  if (videosErr) throw videosErr;

  const { data: { user } } = await supabase.auth.getUser();
  
  let subscribedIds = new Set<string>();
  if (user?.id) {
    const { data: subscriptions } = await supabase
      .from('collection_subscriptions')
      .select('collection_id')
      .eq('user_id', user.id);
    subscribedIds = new Set(subscriptions?.map(s => s.collection_id) || []);
  }

  const grouped = videos.reduce((acc, video) => {
    const vid: VideoItem = {
      id: video.id,
      videoId: video.video_id,
      title: video.title,
      thumbnail: video.thumbnail
    };
    acc[video.collection_id] = acc[video.collection_id] || [];
    acc[video.collection_id].push(vid);
    return acc;
  }, {} as Record<string, VideoItem[]>);

  return collections.map((c) => ({
    ...c,
    videos: grouped[c.id!] || [],
    is_subscribed: subscribedIds.has(c.id!)
  }));
};

export const getCollections = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // First, get user's own collections
  const { data: collections, error } = await supabase
    .from('collections')
    .select(`
      id,
      name,
      description,
      thumbnail_url,
      created_at,
      user_id,
      is_public,
      public_id,
      tags,
      categories:collection_categories(
        category:categories(*)
      )
    `)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Get user's subscriptions
  const { data: subscriptions } = await supabase
    .from('collection_subscriptions')
    .select('collection_id')
    .eq('user_id', user.id);

  const subscribedIds = new Set(subscriptions?.map(s => s.collection_id) || []);

  // Get subscribed collections if there are any
  let subscribedCollections = [];
  if (subscribedIds.size > 0) {
    const { data: subCollections, error: subError } = await supabase
      .from('collections')
      .select(`
        id,
        name,
        description,
        thumbnail_url,
        created_at,
        user_id,
        is_public,
        public_id,
        tags,
        categories:collection_categories(
          category:categories(*)
        )
      `)
      .in('id', Array.from(subscribedIds))
      .order('created_at', { ascending: false });

    if (subError) throw subError;
    subscribedCollections = subCollections || [];
  }

  // Combine and format the results
  return [
    ...(collections || []).map(c => ({
      ...c,
      categories: c.categories?.map(cc => cc.category)
    })),
    ...subscribedCollections.map(c => ({
      ...c,
      categories: c.categories?.map(cc => cc.category),
      is_subscribed: true
    }))
  ];
};

export const getCollectionWithVideos = async (collectionId: string) => {
  const { data: collection, error: collectionError } = await supabase
    .from('collections')
    .select(`
      id,
      name,
      description,
      thumbnail_url,
      created_at,
      user_id,
      is_public,
      public_id,
      tags,
      categories:collection_categories(
        category:categories(*)
      )
    `)
    .eq('id', collectionId)
    .single();

  if (collectionError) throw collectionError;

  const { data: videos, error: videosError } = await supabase
    .from('collection_videos')
    .select('*')
    .eq('collection_id', collectionId)
    .order('created_at', { ascending: false });

  if (videosError) throw videosError;

  const { data: { user } } = await supabase.auth.getUser();
  
  let subscription = null;
  if (user?.id) {
    const { data } = await supabase
      .from('collection_subscriptions')
      .select('id')
      .eq('collection_id', collectionId)
      .eq('user_id', user.id)
      .maybeSingle();
    subscription = data;
  }

  return {
    ...collection,
    categories: collection.categories?.map(cc => cc.category),
    videos: videos.map((v) => ({
      id: v.id,
      videoId: v.video_id,
      title: v.title,
      thumbnail: v.thumbnail
    })),
    is_subscribed: !!subscription
  };
};

export const updateCollection = async (collectionId: string, updates: Partial<Collection>) => {
  const { categories, ...collectionUpdates } = updates;

  const { data, error } = await supabase
    .from('collections')
    .update(collectionUpdates)
    .eq('id', collectionId)
    .select()
    .single();

  if (error) throw error;

  if (categories) {
    await supabase
      .from('collection_categories')
      .delete()
      .eq('collection_id', collectionId);

    if (categories.length > 0) {
      await supabase
        .from('collection_categories')
        .insert(
          categories.map(category => ({
            collection_id: collectionId,
            category_id: category.id
          }))
        );
    }
  }

  return data;
};

export const createCollection = async (collection: Partial<Collection>) => {
  const { data, error } = await supabase
    .from('collections')
    .insert([collection])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removeCollection = async (collectionId: string) => {
  const { error } = await supabase
    .from('collections')
    .delete()
    .eq('id', collectionId);

  if (error) throw error;
};

export const addVideoToCollection = async (collectionId: string, video: VideoItem): Promise<boolean> => {
  if (!video.videoId) throw new Error("Missing videoId");

  const { error: videoInsertError } = await supabase
    .from('videos')
    .upsert([
      {
        id: video.videoId,
        title: video.title,
        thumbnail: video.thumbnail
      }
    ], { onConflict: 'id' });

  if (videoInsertError) {
    throw new Error(`Failed to upsert video: ${videoInsertError.message}`);
  }

  const { error } = await supabase
    .from('collection_videos')
    .insert([
      {
        collection_id: collectionId,
        video_id: video.videoId,
        title: video.title,
        thumbnail: video.thumbnail
      }
    ]);

  if (error?.code === '23505') {
    throw new Error("This video is already in the collection");
  } else if (error) {
    throw new Error(`Failed to add video to collection: ${error.message}`);
  }

  return true;
};

export const removeVideoFromCollection = async (collectionId: string, videoId: string) => {
  const { error } = await supabase
    .from('collection_videos')
    .delete()
    .eq('collection_id', collectionId)
    .eq('video_id', videoId);

  if (error) throw error;
};

export const getAllTags = async (): Promise<string[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('collections')
    .select('tags')
    .eq('user_id', user.id);

  if (error) throw error;

  const uniqueTags = new Set<string>();
  data.forEach(collection => {
    if (collection.tags) {
      collection.tags.forEach(tag => uniqueTags.add(tag));
    }
  });

  return Array.from(uniqueTags).sort();
};

export const subscribeToCollection = async (collectionId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('collection_subscriptions')
    .insert([{
      user_id: user.id,
      collection_id: collectionId
    }]);

  if (error) throw error;
};

export const unsubscribeFromCollection = async (collectionId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('collection_subscriptions')
    .delete()
    .eq('user_id', user.id)
    .eq('collection_id', collectionId);

  if (error) throw error;
};